"use client";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Callout, Dialog, Flex, Select, Text, VisuallyHidden, } from "@radix-ui/themes";
import * as React from "react";
import { DialogContent, PrimaryButton, SecondaryButton, SelectContent, SelectItem, SelectTrigger, TextField, } from "./elements";
import { Label } from "./elements";
import { isErrorLike } from "./utils";
import { useInviteUser } from "./api/user";
import { useRoles } from "../api";
/**
 * Used to stub a fake value for the role select. It will be selected by default
 * before the role query resolves, or if the query results in an error. We do
 * this because we need to provide _any_ value to the select to avoid
 * controlled/uncontrolled bugs.
 */
const PLACEHOLDER_ROLE = "_rolePlaceholder";
export function InviteUserDialog({ children }) {
    const [open, setOpen] = React.useState(false);
    const dialogId = toId("invite-user", React.useId());
    const formId = toId(dialogId, "form");
    const inviteUser = useInviteUser();
    const rolesQuery = useRoles({
        query: { initialData: [] },
    });
    const roles = rolesQuery.data;
    const [selectedRole, setSelectedRole] = React.useState(() => getDefaultRole(roles)?.slug || PLACEHOLDER_ROLE);
    React.useEffect(() => {
        // Update the selected role if it's not in the list (eg if the list was
        // previously empty and the query resolved)
        setSelectedRole((selectedRole) => {
            if (roles.find((role) => role.slug === selectedRole)) {
                // if current selected role is in the new list, don't change it
                return selectedRole;
            }
            return getDefaultRole(roles)?.slug || PLACEHOLDER_ROLE;
        });
    }, [roles]);
    const onSubmitForm = (data) => {
        if (inviteUser.isPending || rolesQuery.status !== "success") {
            return;
        }
        inviteUser.mutate({ data }, {
            onSuccess: () => {
                setOpen(false);
            },
        });
    };
    const formErrors = getFormErrors(inviteUser.error);
    useFormFieldFocusOnError(dialogId, inviteUser.error);
    return (_jsxs(Dialog.Root, { open: open, onOpenChange: setOpen, children: [children && _jsx(Dialog.Trigger, { children: children }), _jsxs(DialogContent, { maxWidth: "480px", children: [_jsx(Dialog.Title, { children: "Invite user" }), _jsx(Dialog.Description, { children: "An invitation will be sent to this email address with a link to complete their account." }), _jsx(Flex, { direction: "column", gap: "4", mt: "5", asChild: true, children: _jsxs("form", { id: formId, onSubmit: async (event) => {
                                event.preventDefault();
                                onSubmitForm({
                                    email: event.currentTarget.email.value,
                                    roles: [selectedRole],
                                });
                            }, children: [_jsx(FormField, { rootId: dialogId, name: "email", label: "Email address", error: formErrors.fields.email, required: true, control: (props) => (_jsx(TextField, { ...props, "data-1p-ignore": "true", "data-lpignore": "true", type: "email", autoComplete: "off", placeholder: "Enter an email address" })) }), _jsx(FormField, { rootId: dialogId, name: "role", label: "Role", error: formErrors.fields.role, disabled: rolesQuery.isPending || roles.length <= 1, info: roles.length === 1 ? (_jsxs(_Fragment, { children: ["New users will be invited with the", " ", _jsx(Text, { weight: "bold", children: roles[0].name }), " role, as it is the only one available."] })) : undefined, control: ({ id, "aria-invalid": ariaInvalid, "aria-describedby": ariaDescribedBy, ...props }) => (_jsxs(Select.Root, { ...props, value: selectedRole, onValueChange: setSelectedRole, children: [_jsx(SelectTrigger, { id: id, "aria-invalid": ariaInvalid, "aria-describedby": ariaDescribedBy }), _jsxs(SelectContent, { children: [_jsx(SelectItem, { value: PLACEHOLDER_ROLE, disabled: true, children: "Select a role" }), roles.map((role) => (_jsx(SelectItem, { value: role.slug, children: role.name }, role.slug)))] })] })) })] }) }), formErrors.form ? (_jsx(Callout.Root, { color: "red", mt: "4", mb: "-2", children: _jsx(Callout.Text, { children: formErrors.form }) })) : null, _jsxs(Flex, { mt: "5", gap: "3", justify: "end", children: [_jsx(Dialog.Close, { children: _jsx(SecondaryButton, { disabled: inviteUser.isPending, children: "Cancel" }) }), _jsx(PrimaryButton, { form: formId, loading: inviteUser.isPending, disabled: rolesQuery.isPending || undefined, children: "Invite" })] }), _jsx(VisuallyHidden, { asChild: true, children: _jsx("section", { "aria-live": "polite", children: formErrors.form }) })] }, String(open))] }));
}
function FormField({ rootId, name, label, error, info, control, required, disabled, }) {
    const fieldId = toId(rootId, name);
    const errorId = toId(rootId, name, "error");
    const infoId = toId(rootId, name, "info");
    return (_jsxs(Flex, { direction: "column", gap: "1", children: [_jsx(Label, { htmlFor: fieldId, children: label }), control({
                id: fieldId,
                name,
                "aria-describedby": (() => {
                    const tags = [];
                    if (error) {
                        tags.push(errorId);
                    }
                    if (info) {
                        tags.push(infoId);
                    }
                    if (tags.length === 0) {
                        return undefined;
                    }
                    return tags.join(" ");
                })(),
                "aria-invalid": !!error || undefined,
                required: required || undefined,
                disabled: disabled || undefined,
            }), error ? (_jsx(Text, { color: "red", size: "2", id: errorId, children: error })) : null, info ? (_jsx(Text, { color: "gray", size: "2", id: infoId, mt: "1", children: info })) : null] }));
}
function toId(...parts) {
    return parts.join("-");
}
function getFormErrors(queryError) {
    const formErrors = {
        form: null,
        fields: {
            email: null,
            role: null,
        },
    };
    if (queryError) {
        if (!isErrorLike(queryError)) {
            return {
                ...formErrors,
                form: "An unexpected error occurred. Please try again.",
            };
        }
        switch (queryError.message.toLowerCase()) {
            case "user already exists":
            case "user already invited":
            case "invalid email":
                formErrors.fields.email = queryError.message;
                break;
            case "invalid role":
                formErrors.fields.role = queryError.message;
                break;
            default:
                // TODO handle more cases for various server errors
                formErrors.form =
                    "There was an error inviting this user. Please refresh the page and try again.";
                break;
        }
    }
    return formErrors;
}
function useFormFieldFocusOnError(dialogId, queryError) {
    React.useEffect(() => {
        const fieldErrors = getFormErrors(queryError).fields;
        for (const [name, error] of Object.entries(fieldErrors)) {
            if (error) {
                const fieldElement = document.getElementById(toId(dialogId, name));
                if (fieldElement) {
                    fieldElement?.focus();
                    if ("select" in fieldElement) {
                        fieldElement.select();
                    }
                }
                break;
            }
        }
    }, [dialogId, queryError]);
}
function getDefaultRole(roles) {
    return roles.find((role) => role.default) || roles[0];
}
//# sourceMappingURL=invite-user-dialog.js.map